import { Component, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import _ from 'lodash';

import { Account } from '../../models/account';
import { CmsTemplateCopy } from '../../models/content';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';
import { ContentTemplateCopyComponent } from './content-template-copy.component';

/**
 * Template-copy fragments list (PAGE-CMS.md §6). Fragments are CREATED by the
 * seed script from the site's registry — no add/delete here, because a
 * fragment only renders where a template asks for its key: a hand-minted key
 * would be a silent no-op, and deleting one just falls back to the built-in
 * sentence. Edit-only keeps the list honest.
 */
@Component({
  selector: 'app-content-template-copy-list',
  templateUrl: './content-template-copy-list.component.html',
})
export class ContentTemplateCopyListComponent implements OnInit, OnDestroy {
  accountLoading = true;
  loadFailed = false;

  private unsubscribe$ = new Subject<void>();
  account$: Observable<Account>;

  constructor(
    private modalService: ModalService,
    private accountService: AccountService,
    private dialogService: DialogService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {
    this.accountService.ensureFullAccount().subscribe({
      next: () => (this.accountLoading = false),
      error: (err) => {
        this.accountLoading = false;
        this.loadFailed = true;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  fragmentsAsList(content: any): CmsTemplateCopy[] {
    const data = content?.templateCopy;
    if (!data) return [];
    const list = (Array.isArray(data) ? data.filter(Boolean) : Object.values(data).filter(Boolean)) as CmsTemplateCopy[];
    return _.sortBy(list, (f) => f?.name ?? '');
  }

  onEdit(fragment: CmsTemplateCopy): void {
    const ref = this.dialogService.open(ContentTemplateCopyComponent, {
      // Esc would close the editor and silently discard unsaved changes.
      closeOnEscape: false,
      header: `Edit ${fragment.name || fragment.key}`,
      width: '46rem',
      baseZIndex: 11000,
      data: { fragment: _.cloneDeep(fragment) },
    });
    ref.onClose.subscribe({
      next: (result: { saved: boolean; fragment: any } | undefined) => {
        if (!result?.saved) return;
        this.accountService
          .patchContentItem('templateCopy', result.fragment)
          .pipe(take(1))
          .subscribe({ error: (err) => this.modalService.error(err?.error?.error || err) });
      },
    });
  }
}
